import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiEdit2, FiFilePlus, FiFileText, FiMail, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './PendingQuotes.css';

const API_URL = 'https://valleyapi-dr9y.onrender.com/api/control/quotes';
const PAGE_SIZE = 10;

function parseApiDate(value) {
  if (!value) return null;
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseApiDate(value);
  return date ? date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

const PendingQuotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuotes, setTotalQuotes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingQuote, setSendingQuote] = useState('');
  const [deletingQuote, setDeletingQuote] = useState('');

  const fetchQuotes = useCallback(async (requestedPage = page) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('controlToken');
      const response = await axios.get(`${API_URL}?page=${requestedPage}&limit=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(response.data.quotes || []);
      setTotalPages(Number(response.data.totalPages || 1));
      setTotalQuotes(Number(response.data.totalQuotes || 0));
    } catch (requestError) {
      setQuotes([]);
      setError(requestError.response?.data?.error || 'Pending quotes could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchQuotes(page); }, [fetchQuotes, page]);

  const groupedQuotes = useMemo(() => {
    const groups = new Map();
    quotes.forEach((quote) => {
      const key = quote.numero_cotizacion || `quote-${quote.idcotizacion}`;
      if (!groups.has(key)) {
        groups.set(key, {
          numero_cotizacion: quote.numero_cotizacion,
          client_email: quote.client_email,
          client_name: quote.client_name,
          client_category_territory: quote.client_category_territory,
          status: quote.status || 'Pending Quote',
          owner_name: quote.owner_name,
          created_at: quote.created_at,
          last_update: quote.last_update,
          lost_reason: quote.lost_reason,
          products: [],
        });
      }
      groups.get(key).products.push({
        idcotizacion: quote.idcotizacion,
        brand: quote.brand,
        product_sku: quote.product_sku,
        product_price: quote.product_price,
        product_quantity: quote.product_quantity,
        delivery_time: quote.delivery_time,
      });
    });
    return [...groups.values()];
  }, [quotes]);

  const isOldQuote = (lastUpdate) => {
    const date = parseApiDate(lastUpdate);
    return date ? date < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : false;
  };

  const handleSendEmail = async (group) => {
    if (!window.confirm(`Send quote #${group.numero_cotizacion} to ${group.client_email || 'the client'}?`)) return;
    setSendingQuote(group.numero_cotizacion);
    try {
      const token = localStorage.getItem('controlToken');
      await axios.post(`${API_URL}/send-email`, { numero_cotizacion: group.numero_cotizacion }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Quote #${group.numero_cotizacion} sent successfully`);
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Failed to send quote email');
    } finally {
      setSendingQuote('');
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete quote #${group.numero_cotizacion} and its ${group.products.length} product${group.products.length === 1 ? '' : 's'}?`)) return;
    setDeletingQuote(group.numero_cotizacion);
    try {
      const token = localStorage.getItem('controlToken');
      await axios.delete(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: group.products.map((product) => product.idcotizacion) },
      });
      toast.success(`Quote #${group.numero_cotizacion} deleted`);
      if (groupedQuotes.length === 1 && page > 1) setPage((current) => current - 1);
      else fetchQuotes(page);
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Quote could not be deleted');
    } finally {
      setDeletingQuote('');
    }
  };

  return (
    <div className="pq-page">
      <header className="pq-header"><div><span>Sales pipeline</span><h1>Pending Quotes</h1><p>The 10 most recently updated quote groups appear first.</p></div><Link to="/control/pending-quotes/create" className="pq-primary"><FiFilePlus />Create quote</Link></header>
      <div className="pq-toolbar"><div><strong>{totalQuotes}</strong><span>Total quote groups</span></div><div className="pq-legend"><span /><span>Needs follow-up: no update in 7 days</span></div><button type="button" onClick={() => fetchQuotes(page)} disabled={loading}><FiRefreshCw />Refresh</button></div>
      {error && <div className="pq-alert" role="alert"><FiAlertTriangle /><span>{error}</span><button type="button" onClick={() => fetchQuotes(page)}>Try again</button></div>}
      <div className="pq-table-wrap">
        <table className="pq-table"><thead><tr><th>Quote</th><th>Owner</th><th>Client</th><th>Status</th><th>Products</th><th>Updated</th><th><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{loading ? Array.from({ length: 4 }, (_, index) => <tr key={index} className="pq-skeleton"><td colSpan="7"><span /></td></tr>) : groupedQuotes.length ? groupedQuotes.map((group) => {
            const old = isOldQuote(group.last_update || group.created_at);
            const busy = sendingQuote === group.numero_cotizacion || deletingQuote === group.numero_cotizacion;
            return <tr key={group.numero_cotizacion} className={old ? 'is-old' : ''}>
              <td><strong className="pq-quote-number">#{group.numero_cotizacion}</strong><span className="pq-secondary">Created {formatDate(group.created_at)}</span></td>
              <td>{group.owner_name || '—'}</td>
              <td><strong className="pq-client-name">{group.client_name || 'Unnamed client'}</strong><span className="pq-secondary">{group.client_email || '—'}</span><span className="pq-secondary">{group.client_category_territory || 'No category / territory'}</span></td>
              <td><span className={`pq-status pq-status--${group.status.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{group.status}</span>{group.lost_reason && <span className="pq-lost-reason" title={group.lost_reason}>{group.lost_reason}</span>}</td>
              <td><div className="pq-products">{group.products.slice(0, 3).map((product) => <div key={product.idcotizacion}><strong>{product.brand || '—'}</strong><span>{product.product_sku} × {product.product_quantity}</span><em>{product.product_price == null ? 'Price pending' : `$${Number(product.product_price).toFixed(2)}`}</em></div>)}{group.products.length > 3 && <span className="pq-more">+{group.products.length - 3} more</span>}</div></td>
              <td><span className="pq-updated">{formatDate(group.last_update || group.created_at)}</span>{old && <span className="pq-overdue">Follow up</span>}</td>
              <td><div className="pq-actions"><button type="button" onClick={() => navigate(`/control/pending-quotes/edit/${group.numero_cotizacion}`)} title="Edit quote" disabled={busy}><FiEdit2 /></button><button type="button" onClick={() => handleSendEmail(group)} title="Send email" disabled={busy}><FiMail /></button><button type="button" className="is-danger" onClick={() => handleDelete(group)} title="Delete quote" disabled={busy}><FiTrash2 /></button></div></td>
            </tr>;
          }) : <tr><td colSpan="7"><div className="pq-empty"><FiFileText /><strong>No quote groups found</strong><span>Create a quote to start tracking client opportunities.</span></div></td></tr>}</tbody>
        </table>
      </div>
      {totalPages > 1 && <nav className="pq-pagination" aria-label="Pending quotes pages"><span>Page {page} of {totalPages}</span><div><button type="button" onClick={() => setPage((current) => current - 1)} disabled={page === 1 || loading}><FiChevronLeft />Previous</button>{Array.from({ length: totalPages }, (_, index) => index + 1).filter((number) => number === 1 || number === totalPages || Math.abs(number - page) <= 1).map((number, index, visible) => <React.Fragment key={number}>{index > 0 && number - visible[index - 1] > 1 && <span>…</span>}<button type="button" className={number === page ? 'is-active' : ''} onClick={() => setPage(number)} disabled={loading}>{number}</button></React.Fragment>)}<button type="button" onClick={() => setPage((current) => current + 1)} disabled={page === totalPages || loading}>Next<FiChevronRight /></button></div></nav>}
    </div>
  );
};

export default PendingQuotes;
